<template>
  <SelectRoot v-model="selected" :disabled="disabled">
    <SelectTrigger
      class="app-select__trigger"
      :class="{ 'app-select__trigger--placeholder': !selected }"
    >
      <span class="app-select__value">
        {{ selectedLabel || placeholder }}
      </span>
      <span class="app-select__chevron">
        <svg
          width="10"
          height="6"
          viewBox="0 0 10 6"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 1L5 5L9 1"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
    </SelectTrigger>

    <SelectPortal>
      <SelectContent
        class="app-select__content"
        :side-offset="4"
        position="popper"
      >
        <SelectViewport>
          <SelectItem
            v-for="item in normalizedItems"
            :key="item.value"
            :value="item.value"
            class="app-select__item"
          >
            <SelectItemText>{{ item.label }}</SelectItemText>
          </SelectItem>
        </SelectViewport>
      </SelectContent>
    </SelectPortal>
  </SelectRoot>
</template>

<script setup>
  import {
    SelectContent,
    SelectItem,
    SelectItemText,
    SelectPortal,
    SelectRoot,
    SelectTrigger,
    SelectViewport,
  } from "reka-ui"

  defineOptions({ name: "AppSelect" })

  const props = defineProps({
    modelValue: {
      type: [String, Number, Object],
      default: null,
    },
    items: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: "Select...",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    valueAttribute: {
      type: String,
      default: "value",
    },
    labelAttribute: {
      type: String,
      default: "label",
    },
  })

  const emit = defineEmits(["update:modelValue"])

  const selected = computed({
    get: () => props.modelValue,
    set: (val) => emit("update:modelValue", val),
  })

  const normalizedItems = computed(() =>
    props.items.map((item) => {
      if (typeof item === "string" || typeof item === "number") {
        return { value: String(item), label: String(item) }
      }
      return {
        value: String(item[props.valueAttribute] ?? item.value ?? item.label),
        label: String(item[props.labelAttribute] ?? item.label ?? item.value),
      }
    }),
  )

  const selectedLabel = computed(() => {
    if (!props.modelValue && props.modelValue !== 0) return null
    const match = normalizedItems.value.find(
      (item) => item.value === String(props.modelValue),
    )
    return match?.label ?? String(props.modelValue)
  })
</script>

<style scoped>
  .app-select__trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    width: 100%;
    height: 2.1875rem;
    padding: 0 0.625rem;
    background: linear-gradient(180deg, #272727 0%, #191919 100%);
    border: 1px solid #000;
    border-radius: 5px;
    color: #fff;
    font-family: Inter, sans-serif;
    font-size: 0.8125rem;
    font-weight: 700;
    cursor: pointer;
    outline: none;
    white-space: nowrap;
    overflow: hidden;
  }

  .app-select__trigger:hover {
    background: linear-gradient(180deg, #2f2f2f 0%, #212121 100%);
  }

  .app-select__trigger:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.3);
    outline-offset: 2px;
  }

  .app-select__trigger[data-disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .app-select__trigger--placeholder .app-select__value {
    color: rgba(255, 255, 255, 0.5);
  }

  .app-select__value {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: left;
  }

  .app-select__chevron {
    flex-shrink: 0;
    color: #fff;
    transition: transform 0.2s ease;
  }

  .app-select__trigger[data-state="open"] .app-select__chevron {
    transform: rotate(180deg);
  }
</style>

<!-- Unscoped: portal content renders outside component DOM, scoped CSS can't reach it -->
<style>
  .app-select__content {
    background: linear-gradient(180deg, #272727 0%, #191919 100%) !important;
    border: 1px solid #000 !important;
    border-radius: 5px !important;
    overflow: hidden !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5) !important;
    min-width: var(--reka-select-trigger-width) !important;
    width: var(--reka-select-trigger-width) !important;
    z-index: 9999 !important;
  }

  .app-select__item {
    display: flex !important;
    align-items: center !important;
    padding: 0.5rem 0.625rem !important;
    font-family: Inter, sans-serif !important;
    font-size: 0.8125rem !important;
    font-weight: 700 !important;
    color: rgba(255, 255, 255, 0.7) !important;
    cursor: pointer !important;
    outline: none !important;
    user-select: none !important;
    background: transparent !important;
  }

  .app-select__item[data-highlighted] {
    color: #fff !important;
    background: rgba(255, 255, 255, 0.08) !important;
  }

  .app-select__item[data-state="checked"] {
    color: #fff !important;
  }
</style>
